import PropTypes from "prop-types";
import React from "react";
import BottomSheetModal from "./BottomSheetModal";
import { SuccessIcon, StatusPill, PointsBadge, Divider } from "./SheetPrimitives";

/* -------------------------------------------------------------------------
 * ActivityDetailModal — generic detail sheet for a single activity-log
 * entry (dining reward, points redeemed, voucher redeemed, etc). Opened
 * from a row in Profile's Activity table. Built on the shared
 * BottomSheetModal shell.
 * ---------------------------------------------------------------------- */

// Helper function to strip HTML tags from description
const stripHtmlTags = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const isEmptyDisplayValue = (value) => {
  if (value === null || value === undefined) return true;
  const text = stripHtmlTags(String(value)).trim();
  return !text || text.toLowerCase() === "default" || text.includes("App\\Models");
};

const cleanText = (value, fallback = "") => {
  if (isEmptyDisplayValue(value)) return fallback;
  return stripHtmlTags(String(value)).trim();
};

const formatLabel = (value) => (
  cleanText(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatChangeValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number" && (value === 0 || value === 1)) return value === 1 ? "Yes" : "No";
  if (typeof value === "string") {
    const text = cleanText(value);
    if (!text) return "—";
    if (text === "0" || text === "1") return text === "1" ? "Yes" : "No";
    if (text.startsWith("{") || text.startsWith("[")) return "Updated";
    return text;
  }
  if (typeof value === "object") return "Updated";
  return String(value);
};

const DetailRow = ({ label, value }) => {
  if (isEmptyDisplayValue(value)) return null;
  return (
    <div className="activity-detail-row">
      <span className="activity-detail-row__label">{label}</span>
      <span className="activity-detail-row__value">{cleanText(value)}</span>
    </div>
  );
};

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DetailRow.defaultProps = {
  value: "",
};

const ActivityDetailModal = ({ activity, isOpen, onClose }) => {
  if (!activity) return null;

  const {
    icon,
    statusLabel,
    title,
    description,
    points,
    pointsTone,
    type,
    event_label: eventLabel,
    performed_by: performedBy,
    subject,
    subject_name: subjectName,
    subject_id: subjectId,
    date,
    relative_date: relativeDate,
    changes,
  } = activity;
  const activityDescription = cleanText(description || title, "Activity recorded");
  const activityType = cleanText(type || statusLabel, "Activity");
  const recordValue = cleanText(subjectName) || cleanText(subjectId);
  const displayDate = cleanText(date) || cleanText(relativeDate);
  const hasFormattedActivity = Boolean(
    type || eventLabel || performedBy || subject || subjectName || subjectId || date || relativeDate || Array.isArray(changes)
  );
  const changeRows = Array.isArray(changes) ? changes : [];
  const modalTitle = hasFormattedActivity ? "Activity" : cleanText(title || activityType, "Activity");

  return (
    <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="adm-title" compact={true}>
      <div className="mc-step-header">
        <p className="mc-eyebrow">Activity Detail</p>
      </div>
      {icon && <SuccessIcon icon={icon} tone="orange" />}
      {!hasFormattedActivity && <StatusPill label={activityType} />}

      <h2 className="mc-title mc-title--center" id="adm-title">
        {modalTitle}
      </h2>
      <p className="mc-description mc-description--center">{activityDescription}</p>

      {hasFormattedActivity && (
        <>
          <Divider />
          <div className="activity-detail-list">
            <DetailRow label="Activity" value={activityDescription} />
            <DetailRow label="Type" value={activityType} />
            <DetailRow label="Event" value={eventLabel} />
            <DetailRow label="Performed by" value={cleanText(performedBy, "System")} />
            <DetailRow label="Subject" value={subject} />
            <DetailRow label="Record" value={recordValue} />
            <DetailRow label="Date and time" value={displayDate} />
          </div>

          <Divider />
          <div className="activity-detail-changes">
            <h3 className="activity-detail-section-title">Changes</h3>
            {changeRows.length > 0 ? (
              <div className="activity-detail-table-wrap">
                <table className="activity-detail-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Previous value</th>
                      <th>New value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeRows.map((change, index) => (
                      <tr key={`${change.field || "field"}-${index}`}>
                        <td>{formatLabel(change.field) || "Field"}</td>
                        <td>{formatChangeValue(change.old_value)}</td>
                        <td>{formatChangeValue(change.new_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="activity-detail-empty">No field changes recorded.</p>
            )}
          </div>
        </>
      )}

      {points !== null && points !== undefined && (
        <PointsBadge value={points} tone={pointsTone} />
      )}
    </BottomSheetModal>
  );
};

ActivityDetailModal.propTypes = {
  activity: PropTypes.shape({
    icon: PropTypes.string,
    statusLabel: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    event_label: PropTypes.string,
    performed_by: PropTypes.string,
    subject: PropTypes.string,
    subject_name: PropTypes.string,
    subject_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    relative_date: PropTypes.string,
    changes: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      old_value: PropTypes.any,
      new_value: PropTypes.any,
    })),
    points: PropTypes.number,
    pointsTone: PropTypes.oneOf(["green", "red"]),
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

ActivityDetailModal.defaultProps = { activity: null };

export default ActivityDetailModal;
